// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ConfigSectionModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import ListItem, { UnorderedList } from "@insite/mobius/Lists";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins/spacing";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import { ConfigurationType } from "@insite/client-framework/Services/ProductServiceV2";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Img, { ImgProps } from "@store/MobiusOverrides/Img/Img";
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
/* Store Custom Code - End */

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasParentProductId;

/* Store Custom Code - Start */
const mapStateToProps = (state: ApplicationState, ownProps: HasParentProductId) => {
    const configuration = getProductState(state, ownProps.parentProductId).value?.detail?.configuration?.configSections;
    const storeKits = getProductState(state, ownProps.parentProductId).value?.properties?.productsInKit;
    const productState = getProductState(state, ownProps.parentProductId).value; // Store Custom Code

    const kits = storeKits
        ? JSON.parse(storeKits).map((kit: KitModel) => {
              configuration?.forEach(config => {
                  const configProduct = config?.sectionOptions;
                  if (configProduct && configProduct?.length > 0 && configProduct[0]?.productId === kit.ProductId) {
                      kit.ProductKitSectionDescription = configProduct[0].description;
                  }
              });
              return kit;
          })
        : [];
    return { kits, configurationType: productState?.configurationType }; // Store Custom Code
};

export interface KitModel {
    ProductId: string;
    ProductKitSectionOptionId: string;
    ProductKitSectionId: string;
    ProductKitSectionName: string;
    ProductSku: string;
    ProductKitSectionDescription: string;
    ProductUrl: string;
    ProductImageUrl: string;
}
/* Store Custom Code - End */

export interface ProductDetailsKitSectionOptionsStyles {
    wrapper?: InjectableCss;
    configSectionWrapper?: LinkPresentationProps; // Store Custom Code
    configSectionLabel?: TypographyPresentationProps;
    sectionOptionText?: TypographyPresentationProps;
    // Starter Custom Code
    list?: InjectableCss;
    // Store Custom Code
    sku?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    kitImage?: GridItemProps;
    kitDescription?: GridItemProps;
    img?: ImgProps;
    kitDescriptionWrapper?: InjectableCss;
}

export const productDetailsKitSectionOptionsStyles: ProductDetailsKitSectionOptionsStyles = {
    wrapper: {
        css: css`
            ${x =>
                spacingStyles(
                    x.theme.design.product.detail.contentSpacing,
                    x.theme.design.product.detail.contentSpacing,
                )};
        `,
    },
    /* Store Custom Code - Start */
    configSectionWrapper: {
        css: css`
            margin-bottom: ${pxToRem(25)};
        `,
    },
    /* Store Custom Code - End */
    configSectionLabel: {
        css: css`
            ${x => x.theme.formField.defaultProps?.labelProps?.css};
        `,
    },
    list: {
        css: css`
            list-style-type: none;
            padding-left: 0;
        `,
    },
    // Store Custom Code
    sku: {
        forwardAs: "p",
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    description: {
        forwardAs: "p",
    },
    kitImage: {
        width: [12, 3, 3, 3, 3],
    },
    kitDescription: {
        width: [12, 9, 9, 9, 9],
        css: css`
            text-align: left;
            flex-direction: column;
        `,
    },
    kitDescriptionWrapper: {
        css: css`
            margin: auto 0;
            padding: 0 ${pxToRem(15)};
        `,
    },
    img: {
        css: css`
            width: 100%;
            height: auto;
        `,
    },
};

const styles = productDetailsKitSectionOptionsStyles;

/* Store Custom Code - Start */
const ProductDetailsKitSectionOptions = ({ kits, configurationType }: Props) => {
    if (!kits || kits?.length === 0) {
        return null;
    }

    if (configurationType === ConfigurationType.Standard) {
        return null;
    }

    const kitContainer = (kit: KitModel) => {
        const kitDescription: GridItemProps = { css: styles.kitDescription?.css, width: kit.ProductImageUrl ? 9 : 12 };
        return (
            <Link {...styles.configSectionWrapper} href={kit.ProductUrl} key={kit.ProductKitSectionOptionId}>
                <Typography {...styles.configSectionLabel}>{kit.ProductKitSectionName}:</Typography>
                <GridContainer>
                    {kit.ProductImageUrl && (
                        <GridItem {...styles.kitImage}>
                            <Img
                                {...styles.img}
                                src={kit.ProductImageUrl}
                                altText={kit.ProductKitSectionDescription}
                                loading="eager"
                            />
                        </GridItem>
                    )}
                    <GridItem {...kitDescription}>
                        <StyledWrapper {...styles.kitDescriptionWrapper}>
                            <Typography {...styles.sku}>{kit.ProductSku}</Typography>
                            <Typography {...styles.description}>{kit.ProductKitSectionDescription}</Typography>
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </Link>
        );
    };

    return <StyledWrapper {...styles.wrapper}>{kits.map((kit: KitModel) => kitContainer(kit))}</StyledWrapper>;
};
/* Store Custom Code - End */

const widgetModule: WidgetModule = {
    component: withParentProductId(connect(mapStateToProps)(ProductDetailsKitSectionOptions)),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
